import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, api } from "@/lib/api";
import { markDemoVisit } from "@/lib/demoVisitor";
import WaitlistForm from "@/components/WaitlistForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProfileTypePicker } from "@/components/ProfileTypePicker";
import { FinloLogo } from "@/components/FinloLogo";
import { ShieldCheck, Lock, TrendingDown, Sparkles, Loader2 } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1712873068978-eb0cb20d1b62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBmaW5hbmNpYWwlMjBkaXN0cmljdCUyMHNreWxpbmUlMjBkYXJrfGVufDB8fHx8MTc5MDc2NzkwN3ww&ixlib=rb-4.1.0&q=85";

export default function AuthWall() {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(() => (sessionStorage.getItem("finlo_auth_tab") === "register" ? "register" : "login"));
  const [signupsOpen, setSignupsOpen] = useState(null);
  useEffect(() => {
    sessionStorage.removeItem("finlo_auth_tab");
    api.get("/auth/signup-status").then(({ data }) => setSignupsOpen(data.open)).catch(() => setSignupsOpen(false));
  }, []);
  useEffect(() => { if (signupsOpen === false) setTab("login"); }, [signupsOpen]);
  const [form, setForm] = useState({ name: "", email: "", password: "", profile_type: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (tab === "register" && !form.profile_type) {
      setError("Please choose a profile type to continue");
      return;
    }
    setLoading(true);
    try {
      if (tab === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.profile_type);
      toast.success(tab === "login" ? "Welcome back to Finlo" : "Account created — welcome to Finlo");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const demoLogin = async (source = "button") => {
    setError("");
    setLoading(true);
    try {
      await login("demo@ventiqa.co.uk", "demo123");
      api.post("/analytics/demo-visit", { visitor_id: markDemoVisit(), source }).catch(() => {});
      toast.success("Signed in with the demo account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const demoStarted = useRef(false);
  useEffect(() => {
    if (window.location.pathname !== "/demo" || demoStarted.current) return;
    demoStarted.current = true;
    window.history.replaceState({}, "", "/");
    demoLogin("link");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#0D0D0D]">
      {/* Visual panel */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_IMG} alt="City of London financial district at night" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/70 to-[#0D0D0D]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/40 to-transparent" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <FinloLogo size="lg" testid="auth-hero-logo" />
          <div className="max-w-md">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896] mb-4">UK Open Banking · FCA regulated</p>
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1]">
              Take control of every pound.
            </h1>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Track spending, catch runaway subscriptions and set smart alerts — all your UK accounts in one calm, secure place.
            </p>
            <div className="mt-8 space-y-3">
              {[
                { icon: ShieldCheck, t: "Bank-grade encryption on every connection" },
                { icon: TrendingDown, t: "Spot recurring charges draining your balance" },
                { icon: Sparkles, t: "Real-time alerts when a category goes over budget" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                  <f.icon className="h-4 w-4 text-[#00C896] shrink-0" />
                  <span>{f.t}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500">A product by Ventiqa Solutions Limited</p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8"><FinloLogo size="lg" testid="auth-mobile-logo" /></div>

          <div className="inline-flex items-center gap-1.5 rounded-full border border-[#00C896]/30 bg-[#00C896]/10 px-3 py-1 text-xs text-[#4DE3BF] mb-5">
            <Lock className="h-3 w-3" /> Secure access required
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight">
            {tab === "login" ? "Sign in to Finlo" : "Create your account"}
          </h2>
          <p className="text-slate-400 text-sm mt-2">
            {tab === "login" ? "Everything is locked behind sign-in for your safety." : "One account for all your UK finances."}
          </p>

          {signupsOpen && (
            <div className="mt-6 grid grid-cols-2 rounded-xl bg-[#14181D] p-1 border border-white/8">
              {["login", "register"].map((t) => (
                <button
                  key={t}
                  data-testid={`auth-tab-${t}`}
                  onClick={() => { setTab(t); setError(""); }}
                  className={`rounded-lg py-2 text-sm font-medium transition-all ${
                    tab === t ? "bg-[#00C896] text-[#04140D]" : "text-slate-400 hover:text-white"
                  }`}
                >
                  {t === "login" ? "Log in" : "Register"}
                </button>
              ))}
            </div>
          )}
          {signupsOpen === false && (
            <div data-testid="signup-coming-soon" className="mt-6 rounded-xl border border-[#00C896]/25 bg-[#00C896]/[0.07] px-4 py-3">
              <div className="flex items-start gap-3">
                <Sparkles className="h-4 w-4 text-[#00C896] mt-0.5 shrink-0" />
                <p className="text-sm text-slate-300">
                  <span className="font-semibold text-white">New accounts: coming soon.</span> Leave your email and we'll let you know when sign-ups open. Or explore everything with the demo below.
                </p>
              </div>
              <WaitlistForm />
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {tab === "register" && (
              <div>
                <Label className="text-slate-300 text-sm">Full name</Label>
                <Input
                  data-testid="auth-name-input"
                  value={form.name}
                  onChange={upd("name")}
                  required
                  placeholder="Alex Morgan"
                  className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white h-11 focus-visible:ring-[#00C896]"
                />
              </div>
            )}
            <div>
              <Label className="text-slate-300 text-sm">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={form.email}
                onChange={upd("email")}
                required
                placeholder="you@example.co.uk"
                className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white h-11 focus-visible:ring-[#00C896]"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                value={form.password}
                onChange={upd("password")}
                required
                minLength={6}
                placeholder="••••••••"
                className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white h-11 focus-visible:ring-[#00C896]"
              />
            </div>

            {tab === "register" && (
              <div data-testid="register-profile-type">
                <Label className="text-slate-300 text-sm">I'm a…</Label>
                <div className="mt-1.5">
                  <ProfileTypePicker
                    value={form.profile_type}
                    onChange={(v) => { setForm({ ...form, profile_type: v }); setError(""); }}
                    testPrefix="register-profile-type"
                  />
                </div>
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                {error}
              </div>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tab === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-600">
            <div className="h-px flex-1 bg-white/10" /> OR <div className="h-px flex-1 bg-white/10" />
          </div>

          <Button
            data-testid="demo-login-button"
            variant="outline"
            onClick={() => demoLogin("button")}
            disabled={loading}
            className="w-full h-11 rounded-xl border-[#00C896]/40 bg-transparent text-[#4DE3BF] hover:bg-[#00C896]/10 hover:text-[#7FEAD0]"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Try the 1-click demo (pre-loaded data)
          </Button>

          <p className="mt-8 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} Finlo — a product by Ventiqa Solutions Limited
          </p>
        </motion.div>
      </div>
    </div>
  );
}
